import { useEffect, useRef, useState } from "react";
import { asset } from "../../lib/assets";

// Ported from the Lovable "violerts-launchpad" AnimatedMapBackground (VWA-2898).
// Images: hero-map-v4 is served as AVIF (~100 KB) with a WebP fallback, and the
// map pin is a 256 px WebP - same pixels at the sizes they are shown. Used by the home hero and the
// sign-in / sign-up pages.


// Pins are desktop-only; on phones the map shows no animation.
const MARKER_COUNT = 8;

const mapEvents = [
  { label: "New DOB Violation", position: "left-[27%] top-[10%]" },
  { label: "New DOB Complaint", position: "left-[43%] top-[10%]" },
  { label: "New Filing found", position: "left-[63%] top-[10%]" },
  { label: "DOB Violation Status Change", position: "left-[77%] top-[10%]" },
  { label: "New HPD Complaint", position: "left-[35%] top-[26%]" },
  { label: "New ECB Violation", position: "left-[54%] top-[26%]" },
  { label: "New OATH Hearing", position: "left-[21%] top-[28%]" },
  { label: "Stop Work Order Issued", position: "left-[73%] top-[30%]" },
];

const eventLabels = [
  "New DOB Violation",
  "New DOB Complaint",
  "New ECB Violation",
  "New HPD Violation",
  "New HPD Complaint",
  "New OATH Hearing",
  "New FDNY Violation",
  "New Filing found",
  "Stop Work Order Issued",
  "New 311 Complaint",
  "Temp CO Issued",
  "Final CO Issued",
  "DOB Violation Status Change",
  "DOB Complaint Status Change",
  "ECB Violation Status Change",
  "HPD Violation Status Change",
  "HPD Complaint Status Change",
  "FDNY Violation Status Change",
  "Filing Status Change",
  "311 Complaint Status Change",
  "Upcoming OATH Hearing",
  "Upcoming ECB/OATH Hearing",
  "Upcoming Permit Expiration",
];

// Real NYC street blocks, matched to the image by georeferencing it against a reference map.
// Every spot was checked against the image's own water mask and OpenStreetMap park polygons,
// so none falls in water, a park or New Jersey; each pair sits at least 15 units apart.
const landSpots = [
  [27, 10], [43, 10], [63, 10], [77, 10],
  [35, 26], [54, 26], [21, 28], [73, 30],
  [81, 46], [73, 64], [47, 78], [64, 83],
  [14, 83], [81, 83],
];

function pickSlot(slots, index) {
  const others = slots.filter((_, i) => i !== index).map((s) => landSpots[s.spot]);
  const current = slots[index]?.spot;
  const options = landSpots
    .map((_, i) => i)
    .filter((i) => i !== current)
    .filter((i) => others.every(([x, y]) => Math.hypot(landSpots[i][0] - x, landSpots[i][1] - y) > 12));
  const pool = options.length ? options : landSpots.map((_, i) => i).filter((i) => i !== current);
  return {
    spot: pool[Math.floor(Math.random() * pool.length)] ?? 0,
    label: Math.floor(Math.random() * eventLabels.length),
  };
}

// The element whose size the map covers and whose [data-map-protected] children pins avoid:
// the closest [data-map-section] (used where the map sits in a wrapper, e.g. the auth pages),
// otherwise the direct parent (the home hero <section>).
function findSection(node) {
  return node?.closest("[data-map-section]") ?? node?.parentElement ?? null;
}

export function AnimatedMapBackground() {
  const backgroundRef = useRef(null);
  const [mapSize, setMapSize] = useState();
  const [randomSlots, setRandomSlots] = useState(null);

  useEffect(() => {
    // Decoration only: where these browser APIs are missing (very old browsers, jsdom) the page
    // simply renders the still map instead of failing - it hosts the sign-in / sign-up forms.
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(min-width: 768px)");
    const sync = () => setRandomSlots(query.matches ? Array.from({ length: MARKER_COUNT }, (_, i) => ({ spot: i, label: i % eventLabels.length })) : null);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  const reroll = (index) => setRandomSlots((slots) => {
    if (!slots) return slots;
    const next = [...slots];
    next[index] = pickSlot(slots, index);
    return next;
  });

  useEffect(() => {
    const section = findSection(backgroundRef.current);
    if (!section || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(() => {
      // Measure the full box (including padding) so the map always covers the edges.
      const { width, height } = section.getBoundingClientRect();
      // Preserve the supplied crop, with only a 2% margin for subtle drift.
      const scale = Math.max(width / 1920, height / 1288) * 1.02;
      const mapWidth = 1920 * scale;
      const overflowX = mapWidth - width;
      // Pan the view left: shift the image right so more of Manhattan (the map's
      // left side) is visible. Desktop reveals down to a 6% left crop; phones
      // frame Manhattan near center instead. Keep a 2% margin to the edge so
      // the drift animation can never expose a border.
      const target = width < 768 ? 0.11 : 0.06;
      const offsetX = overflowX > 0 ? Math.max(Math.min(overflowX / 2 - mapWidth * target, overflowX / 2 - mapWidth * 0.02), 0) : 0;
      setMapSize({ width: mapWidth, height: 1288 * scale, offsetX });
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = findSection(backgroundRef.current);
    if (!section) return undefined;
    let frame = 0;
    let lastCheck = 0;
    const protectContent = (time) => {
      if (time - lastCheck >= 100) {
        lastCheck = time;
        const protectedAreas = Array.from(section.querySelectorAll("[data-map-protected]"), (element) => ({
          bounds: element.getBoundingClientRect(),
          clearance: element.dataset.mapProtected === "exact" ? 0 : 24,
        }));
        section.querySelectorAll(".hero-map-event").forEach((event) => {
          const overlaps = Array.from(event.children).some((child) => {
            const bounds = child.getBoundingClientRect();
            return protectedAreas.some(({ bounds: area, clearance }) => bounds.left < area.right + clearance && bounds.right > area.left - clearance &&
              bounds.top < area.bottom + clearance && bounds.bottom > area.top - clearance);
          });
          event.style.visibility = overlaps ? "hidden" : "visible";
        });
      }
      frame = requestAnimationFrame(protectContent);
    };
    frame = requestAnimationFrame(protectContent);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (<div ref={backgroundRef} className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="hero-map-stage" style={{ width: mapSize?.width, height: mapSize?.height, transform: `translate(calc(-50% + ${mapSize?.offsetX ?? 0}px), -50%)`, opacity: mapSize ? 1 : 0 }} aria-hidden="true">
      <div className="hero-map-drift absolute inset-0">
        {/* src is assigned after mount: React creates the <img> before it is placed inside <picture>,
              and an <img src> created that early makes Safari fetch the fallback as well as the AVIF. */}
        <picture>
          <source srcSet={asset("hero-map-v4.avif")} type="image/avif" />
          <img ref={(img) => { if (img && !img.getAttribute("src")) img.src = asset("hero-map-v4.webp"); }} alt="Map of New York City showing property violation alert locations" width={1920} height={1288} loading="eager" decoding="async" fetchpriority="high" className="absolute inset-0 h-full w-full object-cover opacity-100" />
        </picture>
        {/* Overlay sits under the pins: it darkens the map but leaves alerts fully bright. */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/50 to-ink/80" aria-hidden="true" />
        <div className="absolute inset-0" aria-hidden="true">
          {mapEvents.map((event, index) => {
            const slot = randomSlots?.[index];
            const label = slot ? eventLabels[slot.label] : event.label;
            const style = {
              "--event-index": index,
              ...(slot ? { left: `${landSpots[slot.spot][0]}%`, top: `${landSpots[slot.spot][1]}%` } : {}),
            };
            return (<div key={index} className={`hero-map-event absolute ${slot ? "" : event.position}`} style={style} onAnimationIteration={(e) => { if (e.target === e.currentTarget) reroll(index); }}>
              <img src={asset("map-signal-v3.webp")} alt="" className="absolute bottom-0 left-0 h-9 w-9 max-w-none -translate-x-1/2 translate-y-1/3 object-contain" />
              <span className="hero-map-label absolute left-5 -top-4 whitespace-nowrap rounded border border-ink-foreground/15 bg-ink/80 px-3 py-1.5 text-xs font-semibold text-ink-foreground shadow-lift backdrop-blur-sm">
                {label}
              </span>
            </div>);
          })}
        </div>
      </div>
    </div>
  </div>);
}
